import { useState } from 'react'
import {
  Card, Typography, Tag, Row, Col, Alert,
  Progress, Checkbox, Button, DatePicker, Popconfirm,
  Form, Input, Select, Space, message, Divider,
} from 'antd'
import {
  CalendarOutlined, LockOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

const TASK_STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'success', OVERDUE: 'error', IN_PROGRESS: 'processing', PENDING: 'warning',
}

const TASK_TEXT_COLORS: Record<string, string> = {
  COMPLETED: '#52c41a', OVERDUE: '#ff4d4f', IN_PROGRESS: '#1677ff', PENDING: '#fa8c16',
}

const MONTH_END_STEPS = [
  { day: 0, label: 'Cut-off circular issued', key: 'day0' },
  { day: 1, label: 'Stock & dispatch freeze', key: 'day1' },
  { day: 2, label: 'Pending bills & accruals booked', key: 'day2' },
  { day: 3, label: 'Payroll, utilities & depreciation', key: 'day3' },
  { day: 4, label: 'GST / TDS reconciliation', key: 'day4' },
  { day: 5, label: 'Schedules & variance analysis', key: 'day5' },
  { day: 6, label: 'TB freeze & MIS circulation', key: 'day6' },
]

export default function ComplianceDashboardPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const [calPeriod, setCalPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [closePeriod, setClosePeriod] = useState<dayjs.Dayjs>(dayjs())
  const [editingTask, setEditingTask] = useState<string | null>(null)
  const [taskForm] = Form.useForm()

  const { data: complianceData } = useQuery({
    queryKey: ['compliance-tasks', plantId, calPeriod?.format('YYYY-MM')],
    queryFn: () =>
      apiClient.get(`${API}/accounting/compliance/tasks`, {
        params: {
          plant_id: plantId,
          month: calPeriod?.month() + 1,
          year: calPeriod?.year(),
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: closeData } = useQuery({
    queryKey: ['month-end-close', plantId, closePeriod?.format('YYYY-MM')],
    queryFn: () =>
      apiClient.get(`${API}/accounting/month-end-close`, {
        params: {
          plant_id: plantId,
          month: closePeriod?.month() + 1,
          year: closePeriod?.year(),
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const updateTaskMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/accounting/compliance/tasks/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-tasks'] })
      message.success('Task updated')
      setEditingTask(null)
      taskForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const markStepMut = useMutation({
    mutationFn: ({ stepKey, done }: { stepKey: string; done: boolean }) =>
      apiClient.post(`${API}/accounting/month-end-close/step`, {
        plant_id: plantId,
        month: closePeriod?.month() + 1,
        year: closePeriod?.year(),
        step_key: stepKey,
        completed: done,
      }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['month-end-close'] }),
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const closePeriodMut = useMutation({
    mutationFn: () =>
      apiClient.post(`${API}/accounting/month-end-close/close-period`, {
        plant_id: plantId,
        month: closePeriod?.month() + 1,
        year: closePeriod?.year(),
      }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['month-end-close'] })
      message.success(`Period ${closePeriod?.format('MMM YYYY')} closed`)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const tasks = Array.isArray(complianceData) ? complianceData : []
  const closeInfo = closeData ?? {}
  const completedSteps = (closeInfo.completed_steps ?? []) as string[]
  const stepsCount = MONTH_END_STEPS.length
  const doneCount = MONTH_END_STEPS.filter(s => completedSteps.includes(s.key)).length
  const pct = Math.round((doneCount / stepsCount) * 100)

  const overdueTasks = tasks.filter((t: any) => t.status === 'OVERDUE')

  return (
    <div style={{ padding: 24 }}>
      <Row style={{ marginBottom: 0 }} align="middle" justify="space-between">
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            <CalendarOutlined style={{ marginRight: 8, color: '#1677ff' }} />
            Compliance & Month-End Close
          </Title>
          <Text type="secondary">Track statutory deadlines and manage period-close checklist</Text>
        </Col>
        <Col>
          <Select value={plantId} onChange={setPlantId} style={{ width: 180 }}
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        {/* Left: Compliance Calendar */}
        <Col span={14}>
          <Card
            title="Compliance Calendar"
            size="small"
            extra={
              <DatePicker picker="month" value={calPeriod} onChange={v => v && setCalPeriod(v)} size="small" />
            }
          >
            {overdueTasks.length > 0 && (
              <Alert
                type="error"
                message={`${overdueTasks.length} overdue task(s) require immediate attention`}
                style={{ marginBottom: 12 }}
                showIcon
              />
            )}

            {tasks.length === 0 && (
              <Text type="secondary">No compliance tasks for this period.</Text>
            )}

            {tasks.map((task: any) => (
              <Card
                key={task.id}
                size="small"
                style={{
                  marginBottom: 8,
                  borderLeft: `4px solid ${TASK_TEXT_COLORS[task.status] ?? '#d9d9d9'}`,
                }}
              >
                <Row justify="space-between" align="top">
                  <Col span={16}>
                    <Space>
                      <Tag color={task.task_type === 'GST' ? 'blue' : task.task_type === 'TDS' ? 'purple' : 'default'}>
                        {task.task_type ?? 'COMPLIANCE'}
                      </Tag>
                      <Text strong>{task.task_name}</Text>
                    </Space>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Due: {task.due_date ? dayjs(task.due_date).format('DD-MMM-YYYY') : '—'}
                      </Text>
                      {task.ref_no && (
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                          Ref: {task.ref_no}
                        </Text>
                      )}
                    </div>
                    {task.completion_notes && (
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{task.completion_notes}</Text>
                    )}
                  </Col>
                  <Col span={8} style={{ textAlign: 'right' }}>
                    <Tag color={TASK_STATUS_COLORS[task.status] ?? 'default'}>
                      {task.status?.replace('_', ' ')}
                    </Tag>
                    <br />
                    <Button
                      size="small"
                      type="link"
                      style={{ padding: 0, marginTop: 4 }}
                      onClick={() => {
                        setEditingTask(task.id)
                        taskForm.setFieldsValue({
                          status: task.status,
                          completion_notes: task.completion_notes,
                          ref_no: task.ref_no,
                        })
                      }}
                    >
                      Update
                    </Button>
                  </Col>
                </Row>

                {editingTask === task.id && (
                  <div style={{ marginTop: 12, padding: '12px', background: '#f9f9f9', borderRadius: 4 }}>
                    <Form form={taskForm} layout="vertical" size="small">
                      <Row gutter={12}>
                        <Col span={8}>
                          <Form.Item name="status" label="Status">
                            <Select
                              options={[
                                { value: 'PENDING', label: 'Pending' },
                                { value: 'IN_PROGRESS', label: 'In Progress' },
                                { value: 'COMPLETED', label: 'Completed' },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="ref_no" label="Reference No">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="completion_notes" label="Notes">
                        <Input.TextArea rows={2} />
                      </Form.Item>
                      <Space>
                        <Button
                          type="primary"
                          size="small"
                          loading={updateTaskMut.isPending}
                          onClick={() =>
                            taskForm.validateFields().then(vals =>
                              updateTaskMut.mutate({ id: task.id, data: vals })
                            )
                          }
                        >
                          Save
                        </Button>
                        <Button size="small" onClick={() => setEditingTask(null)}>Cancel</Button>
                      </Space>
                    </Form>
                  </div>
                )}
              </Card>
            ))}
          </Card>
        </Col>

        {/* Right: Month-End Checklist */}
        <Col span={10}>
          <Card
            title="Month-End Closing Checklist"
            size="small"
            extra={
              <DatePicker picker="month" value={closePeriod} onChange={v => v && setClosePeriod(v)} size="small" />
            }
          >
            {closeInfo.is_closed ? (
              <Alert
                type="success"
                icon={<LockOutlined />}
                message={
                  <Space>
                    <LockOutlined />
                    <span>
                      Period closed by <b>{closeInfo.closed_by}</b> on {dayjs(closeInfo.closed_at).format('DD-MMM-YYYY')}
                    </span>
                  </Space>
                }
                style={{ marginBottom: 16 }}
                showIcon={false}
              />
            ) : (
              <>
                <Progress
                  percent={pct}
                  status={pct === 100 ? 'success' : 'active'}
                  style={{ marginBottom: 16 }}
                />

                {MONTH_END_STEPS.map((step) => {
                  const done = completedSteps.includes(step.key)
                  return (
                    <div
                      key={step.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Space>
                        <Checkbox
                          checked={done}
                          onChange={e => markStepMut.mutate({ stepKey: step.key, done: e.target.checked })}
                        />
                        <div>
                          <Tag style={{ fontSize: 10 }}>Day {step.day}</Tag>
                          <Text style={{ color: done ? '#52c41a' : 'inherit', textDecoration: done ? 'line-through' : 'none' }}>
                            {step.label}
                          </Text>
                        </div>
                      </Space>
                      {done && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    </div>
                  )
                })}

                <Divider style={{ margin: '16px 0' }} />

                <Popconfirm
                  title="Close Period"
                  description={`Are you sure you want to close ${closePeriod?.format('MMM YYYY')}? This will lock all vouchers for this period.`}
                  onConfirm={() => closePeriodMut.mutate()}
                  okText="Close Period"
                  okButtonProps={{ danger: true }}
                  disabled={pct < 100}
                >
                  <Button
                    type="primary"
                    block
                    size="large"
                    danger
                    disabled={pct < 100}
                    loading={closePeriodMut.isPending}
                    icon={<LockOutlined />}
                  >
                    Close Period — {closePeriod?.format('MMM YYYY')}
                  </Button>
                </Popconfirm>
                {pct < 100 && (
                  <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8, fontSize: 12 }}>
                    Complete all checklist steps before closing
                  </Text>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
